import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { LockDateToolTipContent } from "components/LockedDatePicker/LockedDatePicker";
import { USD } from "constants/currency";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";

type WriteOffDateFieldProps = {
  name: string;
  amount: number;
  invoiceDate?: string | null;
};

export const WriteOffDateField = ({
  name,
  amount,
  invoiceDate,
}: WriteOffDateFieldProps) => {
  const entityId = useCurrentEntityId();
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const today = dayjs();
  const lockDate = bookkeepingSettings?.accounting_lock_date
    ? dayjs(bookkeepingSettings.accounting_lock_date)
    : null;

  let minDate = invoiceDate ? dayjs(invoiceDate) : null;

  if (lockDate && (!minDate || !minDate.isAfter(lockDate))) {
    minDate = lockDate.add(1, "day");
  }

  const getDisabledTooltipContent = (date: Date) => {
    if (lockDate && !dayjs(date).isAfter(lockDate)) {
      return <LockDateToolTipContent />;
    }
    return "";
  };

  return (
    <div className="t-flex t-flex-col t-gap-1">
      <DateField
        name={name}
        label="Write-off date"
        required
        minDate={minDate?.toDate()}
        maxDate={today.toDate()}
        getDisabledTooltipContent={getDisabledTooltipContent}
        rightComponent={<CalendarIcon className="t-text-text-30" />}
      />
      <span className="t-text-body-sm t-text-text-30">
        A journal entry moving{" "}
        <AmountSuperScript amount={amount} currencyCode={USD} /> from Accounts
        Receivable to Bad Debt will be created on this date.
      </span>
      <Banner
        type="warning"
        className="t-mt-2"
        description={
          <>
            Once written off, this can't be undone and payments on this invoice
            can't be unlinked. Any future payments can be manually categorised
            as "Bad Debt Recovered".
          </>
        }
      />
    </div>
  );
};
